import React, { memo, useEffect } from 'react'
import { LockedTooltip } from 'apps/fleet-planner/components/load-details/LockedTooltip'
import { IStop } from 'apps/fleet-planner/types/loads'
import { LOAD_FIELDS } from 'apps/fleet-planner/utils/permissions'
import { Bullet } from 'components/atoms'
import { Button, Card, InlineEditContainer } from 'transfix-ui'
import { DISPLAY } from 'constants/display'
import { StopDetails } from './StopDetails'

interface StopDetailsDisplayProps {
  stopType: IStop['stopType']
  sequence: number
  commodity?: string
  weight?: string
  unitQuantity?: string
  unit?: string
  poNumber?: string
  appointmentNumber?: string
  bolNumber?: string
  isExternal?: boolean
  referenceNumber?: string
}

export const StopDetailsDisplay = memo<StopDetailsDisplayProps>(
  ({
    appointmentNumber,
    bolNumber,
    commodity,
    poNumber,
    referenceNumber,
    sequence,
    stopType,
    unit,
    unitQuantity,
    weight,
  }) => {
    const referenceNumberLabel = stopType === 'pickup' ? 'Pickup' : 'Delivery'
    return (
      <span data-test={`stop-details-commodity-${stopType}-${sequence}`}>
        <span>{commodity || 'No commodity'}</span>
        <Bullet />
        <span data-test={`stop-details-weight-${stopType}-${sequence}`}>
          {weight ? weight.toLocaleString() : DISPLAY.empty} lbs
        </span>
        <Bullet />
        <span data-test={`stop-details-unit-quantity-${stopType}-${sequence}`}>
          {unitQuantity || DISPLAY.empty}{' '}
        </span>
        <span data-test={`stop-details-unit-${stopType}-${sequence}`}>
          {unit ? unit.toLowerCase() : 'cases'}
          <br />
        </span>
        <span data-test={`stop-details-po-${stopType}-${sequence}`}>
          {poNumber ? `PO# ${poNumber}` : 'No PO#'}
          <br />
        </span>
        <span data-test={`stop-details-po-${stopType}-${sequence}`}>
          {bolNumber ? `BOL# ${bolNumber}` : 'No BOL#'}
          <br />
        </span>
        <span data-test={`stop-details-appointment-number-${stopType}-${sequence}`}>
          {appointmentNumber ? `Appointment# ${appointmentNumber}` : 'No Appointment #'}
          <br />
        </span>
        <span data-test={`stop-details-reference-number-${stopType}-${sequence}`}>
          {referenceNumber
            ? `${referenceNumberLabel} number# ${referenceNumber}`
            : `No ${referenceNumberLabel} #`}
          <br />
        </span>
      </span>
    )
  }
)

interface IDetailsInlineEdit {
  firstPickup: IStop
  stop: IStop
  currentEditField?: string
  onEditModeRequested(name?: string): void
  onSubmit: () => void
  onCancel: () => void
  onStopUpdate: (stop: Partial<IStop>) => void
  loading?: boolean
  error?: string
  isLocked?: boolean
  isExternal?: boolean
}

export const DetailsInlineEdit = memo<IDetailsInlineEdit>(
  ({
    currentEditField,
    error,
    firstPickup,
    isExternal,
    isLocked,
    loading,
    onCancel,
    onEditModeRequested,
    onStopUpdate,
    onSubmit,
    stop,
  }) => {
    const {
      appointmentNumber,
      bolNumber = '',
      commodity,
      id,
      poNumber,
      referenceNumber,
      stopSequence,
      stopType,
      unit = 'pallets',
      unitQuantity,
      weight,
    } = stop || {}
    const NAME = `${LOAD_FIELDS.details}-${id}`
    const isEditMode = currentEditField === NAME

    // Prevent the inline edit section from closing if there is an error.
    useEffect(() => {
      if (!error && !loading && isEditMode) onEditModeRequested('')
    }, [error, loading, onEditModeRequested])

    return (
      <InlineEditContainer
        isLoading={loading}
        isLocked={isLocked}
        lockedIcon={<LockedTooltip className='locked-icon' />}
        dataTest={`load-details-stop-details-${stopType}-${stopSequence}`}
        name={NAME}
        editModeEnabled={isEditMode}
        resetOnClickOutside={false}
        editMode={
          <Card className='pa4'>
            <StopDetails
              id='inline-edit-details'
              firstPickup={firstPickup}
              variant='load-details'
              stop={stop}
              onStopUpdate={onStopUpdate}
            />
            <div className='tr'>
              <div className='mt4'>
                <Button color='tertiary' size='large' onClick={onCancel}>
                  Cancel
                </Button>
                <Button
                  color='primary'
                  data-test={`stop-details-submit-${stopType}-${stopSequence}`}
                  size='large'
                  className='ml2'
                  onClick={onSubmit}>
                  Save
                </Button>
              </div>
            </div>
          </Card>
        }
        onEditModeRequested={onEditModeRequested}>
        <StopDetailsDisplay
          isExternal={isExternal}
          referenceNumber={referenceNumber}
          bolNumber={bolNumber}
          stopType={stopType}
          sequence={stopSequence}
          commodity={commodity}
          weight={weight}
          unitQuantity={unitQuantity}
          unit={unit}
          poNumber={poNumber}
          appointmentNumber={appointmentNumber}
        />
      </InlineEditContainer>
    )
  }
)
